import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { SolutionHeader, SolutionsStyles } from './Solutions.styles';
import SpotlightCard from '../../common/SpotlightCard/SpotlightCard';
import devLottie from '../../assets/Lottie/it-dev.json';
import customerLottie from '../../assets/Lottie/client.json';
import brokerLottie from '../../assets/Lottie/broker.json';
import solutionLottie from '../../assets/Lottie/solutions.json';
import ReUsableHeaderTitle from '../../common/ReUsableHeaderTitle';
import Intermediari from './parts/Intermediari';
import Clienti from './parts/Clienti';
import ItSolutions from './parts/ItSolutions';

function Solutions() {
  const [activeCard, setActiveCard] = useState(null);
  const navigate = useNavigate();

  const closeBtnRefs = {
    intermediario: useRef(null),
    cliente: useRef(null),
    it: useRef(null),
  };

  const handleClick = (card) => {
    const nextState = activeCard === card ? null : card;
    setActiveCard(nextState);

    if (nextState) {
      setTimeout(() => {
        closeBtnRefs[nextState].current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);
    }
  };

  const cardData = [
    { key: 'intermediario', label: 'Sei Un Intermediario?', lottie: brokerLottie },
    { key: 'cliente', label: 'Sei Un Cliente?', lottie: customerLottie },
    { key: 'it', label: 'Necessiti di Servizi IT', lottie: devLottie },
  ];

  const privateInsurances = [
    {
      name: 'Rc Auto',
      delay: 1,
    },
    {
      name: 'Rc Moto',
      delay: 1.1,

    },
    {
      name: 'Rc Autocarro',
      delay: 1.2,
    },
    {
      name: 'Viaggi',
      delay: 1.3,
    },
    {
      name: 'Tutela Legale',
      delay: 1.4,
    },
    {
      name: 'Casa',
      delay: 1.5,
    },
    {
      name: 'Infortuni Malattia',
      delay: 1.6,
    },
    {
      name: 'TCM',
      delay: 1.7,
    },
  ];

  const rcProfessionalInsurances = [
    {
      name: 'Professioni Giuridiche',
      delay: 1,
    },
    {
      name: 'Professioni Tecniche',
      delay: 1.1,
    },
    {
      name: 'Professioni Mediche',
      delay: 1.2,
    },
    {
      name: 'Professioni Sanitarie',
      delay: 1.3,
    },
    {
      name: 'Cyber Risk',
      delay: 1.4,
    },
    {
      name: 'Infortunio',
      delay: 1.5,
    },
    {
      name: 'Globale Ufficio',
      delay: 1.6,
    },
    {
      name: 'Tutela Legale',
      delay: 1.7,
    },
  ];

  const businessInsurances = [
    {
      name: 'RCT',
      delay: 1,
    },
    {
      name: 'RCO',
      delay: 1.1,
    },
    {
      name: 'Tutela Legale',
      delay: 1.2,
    },
    {
      name: 'D&O',
      delay: 1.3,
    },
    {
      name: 'Incendio',
      delay: 1.4,
    },
    {
      name: 'RCF',
      delay: 1.5,
    },
    {
      name: 'RCI',
      delay: 1.6,
    },
    {
      name: 'Furto',
      delay: 1.7,
    },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Greenia SRL | Soluzioni</title>
        <meta
          name="description"
          content="Le soluzioni Greenia nascono dall’incontro tra innovazione tecnologica e conoscenza del settore assicurativo. Offriamo strumenti pensati per semplificare i processi, migliorare l’esperienza cliente e valorizzare il ruolo di ogni attore dell’ecosistema."
        />

        <meta name="apple-mobile-web-app-title" content="Greenia SRL | Soluzioni" />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="twitter:card"
          content="https://greenia.it/images/logo.png"
        />
        <meta
          name="twitter:description"
          content="Le soluzioni Greenia nascono dall’incontro tra innovazione tecnologica e conoscenza del settore assicurativo. Offriamo strumenti pensati per semplificare i processi, migliorare l’esperienza cliente e valorizzare il ruolo di ogni attore dell’ecosistema."
        />
        <meta name="twitter:title" content="Greenia SRL | Soluzioni" />
        <meta
          name="twitter:image"
          content="https://greenia.it/images/logo.png"
        />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Greenia SRL | Soluzioni" />
        <meta
          property="og:description"
          content="Le soluzioni Greenia nascono dall’incontro tra innovazione tecnologica e conoscenza del settore assicurativo. Offriamo strumenti pensati per semplificare i processi, migliorare l’esperienza cliente e valorizzare il ruolo di ogni attore dell’ecosistema."
        />
        <meta property="og:image" content="https://greenia.it/logo512.png" />
        <meta property="og:url" content="https://greenia.it/" />
        <meta
          property="og:image:secure_url"
          content="https://greenia.it/images/logo.png"
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="Greenia Logo" />
      </Helmet>
      <Navbar />
      <SolutionHeader>
        <div className="d-flex justify-content-center align-items-center">
          <ReUsableHeaderTitle title="Soluzioni" desc="Le soluzioni Greenia nascono dall’incontro tra innovazione tecnologica e conoscenza del settore assicurativo. Offriamo strumenti pensati per semplificare i processi, migliorare l’esperienza cliente e valorizzare il ruolo di ogni attore dell’ecosistema." />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="d-none d-md-flex justify-content-center align-items-center"
        >
          <Lottie
            animationData={solutionLottie}
            loop
            autoplay
            className="lottie-spotlight"
          />
        </motion.div>
      </SolutionHeader>
      <SolutionsStyles className="container">
        <div className="row my-5 g-3">
          {[...cardData]
            .map(({ key, label, lottie }) => {
              const isActive = activeCard === key;
              return (
                <motion.div
                  key={key}
                  layout
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className={`${isActive ? 'col-12' : 'col-12 col-md-4'} ${isActive ? 'order-md-first' : ''}`}
                  style={isActive ? { minHeight: '400px' } : {}}
                >
                  <SpotlightCard spotlightColor={`${isActive ? 'rgba(0, 166, 81, 0) ' : 'rgba(0, 166, 81, 0.2)'}`} className="custom-spotlight-card h-100 position-relative shadow">
                    {!isActive && (
                      <>
                        <Lottie
                          animationData={lottie}
                          loop
                          autoplay
                          className="lottie-spotlight w-25 mb-3 mx-auto"
                        />
                        <p className="mb-3 text-dark fw-semibold spotlight-p text-center">{label}</p>
                        <button onClick={() => handleClick(key)} className="mx-auto d-table">
                          {isActive ? 'Chiudi' : 'Scopri di più'}
                        </button>
                      </>
                    )}
                    {isActive && (
                      <>
                        {key === 'intermediario' && (
                          <Intermediari
                            privateInsurances={privateInsurances}
                            rcProfessionalInsurances={rcProfessionalInsurances}
                            businessInsurances={businessInsurances}
                          />
                        )}
                        {key === 'cliente' && (
                          <Clienti />
                        )}
                        {key === 'it' && (
                          <ItSolutions />
                        )}
                        <div className="position-absolute top-0 end-0 mt-3 me-3">
                          {key === 'intermediario' && (
                            <button onClick={() => navigate('/collabora-con-noi')} className="">
                              Collabora con noi
                            </button>
                          )}
                          {key === 'cliente' && (
                            <button onClick={() => navigate('/login')} className="">
                              Area Clienti
                            </button>
                          )}
                          <button
                            ref={closeBtnRefs[key]}
                            onClick={() => handleClick(key)}
                            className="bg-danger ms-2"
                          >
                            {isActive ? 'Chiudi' : 'Scopri di più'}
                          </button>
                        </div>
                      </>
                    )}
                  </SpotlightCard>
                </motion.div>
              );
            })}
        </div>
      </SolutionsStyles>
      <Footer />

    </>
  );
}

export default Solutions;
